import { useEffect, useState } from "react";
import { X, Loader2, Send, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiErrorField } from "../../lib/api";
import { validate } from "../../lib/validate";
import { Field, inputProps, OptionList } from "./Primitives";

const EMPTY = { company: "", contact: "", email: "", phone: "", site: "", participants: "", preferred_dates: "", format: "", message: "" };
const RULES = {
  company: ["required", ["min", 2]], contact: ["required", ["min", 2]], email: ["required", "email"], phone: ["phone"],
  site: ["required", ["min", 2]], participants: ["required"], preferred_dates: ["required", ["min", 2]],
};

const useClientPrefill = (setValues, setPrefilled) => {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.role !== "client") return;
    setValues((v) => ({ ...v, email: user.email, contact: v.contact || user.name || "" }));
    api.get("/me/client").then(({ data }) => {
      if (!data) return;
      setValues((v) => ({ ...v, company: data.company, contact: data.contact_name, phone: data.phone || "",
        site: [data.country, data.site].filter(Boolean).join(" — ") }));
      setPrefilled(true);
    }).catch(() => {});
  }, [user, setValues, setPrefilled]);
};

export const SessionRequestModal = ({ module, onClose }) => {
  const { t, lang } = useLang();
  const f = t.academy.form;
  const [values, setValues] = useState({ ...EMPTY, format: module.format });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ sending: false, done: null, error: null });
  const [prefilled, setPrefilled] = useState(false);
  useClientPrefill(setValues, setPrefilled);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setVal = (k) => (e) => { setValues((s) => ({ ...s, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };
  const p = (k, req = true) => ({ ...inputProps(`session-${k}`, errors[k], req), value: values[k], onChange: setVal(k) });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, RULES);
    const n = Number(values.participants);
    if (!errs.participants && (!Number.isInteger(n) || n < 1 || n > 500)) errs.participants = ["participantsErr"];
    setErrors(errs);
    if (Object.keys(errs).length) return document.getElementById(`session-${Object.keys(errs)[0]}`)?.focus();
    setState({ sending: true, done: null, error: null });
    try {
      const { data } = await api.post("/academy/requests", { ...values, module_id: module.id, participants: n, lang,
        phone: values.phone.trim() || null, message: values.message.trim() || null });
      setState({ sending: false, done: data.ref, error: null });
    } catch (err) {
      setErrors(apiErrorField(err));
      setState({ sending: false, done: null, error: err?.response?.status === 429 ? f.tooMany : t.errors.generic });
    }
  };

  const title = module[`title_${lang}`] || module.title_fr;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="session-request-modal">
      <div className="panel modal modal-lg" role="dialog" aria-modal="true" aria-labelledby="session-title" onClick={(e) => e.stopPropagation()}>
        <div className="sub-head">
          <p className="eyebrow">{f.title}</p>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label={f.close} autoFocus data-testid="session-modal-close-btn"><X /></button>
        </div>
        <h3 id="session-title" data-testid="session-module-title">{title}</h3>
        {state.done ? (
          <p className="success-msg" role="status" data-testid="session-request-success"><CheckCircle2 aria-hidden="true" />{f.success(state.done)}</p>
        ) : (
          <form onSubmit={submit} noValidate data-testid="session-request-form">
            {prefilled && <p className="info-note" style={{ marginTop: 0 }} data-testid="session-prefilled-note"><Info aria-hidden="true" /><span>{f.prefilled}</span></p>}
            <div className="f-grid">
              <Field id="session-company" label={f.company} error={errors.company} required><input type="text" autoComplete="organization" {...p("company")} /></Field>
              <Field id="session-contact" label={f.contact} error={errors.contact} required><input type="text" autoComplete="name" {...p("contact")} /></Field>
              <Field id="session-email" label={f.email} error={errors.email} required><input type="email" autoComplete="email" {...p("email")} /></Field>
              <Field id="session-phone" label={f.phone} error={errors.phone}><input type="tel" autoComplete="tel" {...p("phone", false)} /></Field>
              <Field id="session-site" label={f.site} error={errors.site} required><input type="text" {...p("site")} /></Field>
              <Field id="session-participants" label={f.participants} error={errors.participants} required><input type="number" min="1" max="500" inputMode="numeric" {...p("participants")} /></Field>
              <Field id="session-preferred_dates" label={f.dates} error={errors.preferred_dates} required><input type="text" placeholder={f.datesPh} {...p("preferred_dates")} /></Field>
              <Field id="session-format" label={f.format}><select {...p("format", false)}><OptionList map={t.academy.formats} /></select></Field>
              <Field id="session-message" label={f.message} error={errors.message} span2><textarea rows={3} {...p("message", false)} /></Field>
            </div>
            {state.error && <p className="error-banner" role="alert" data-testid="session-request-error"><AlertCircle aria-hidden="true" />{state.error}</p>}
            <div className="f-actions">
              <span className="f-note">{t.academy.quoteNote}</span>
              <button type="submit" className="btn btn-primary" disabled={state.sending} data-testid="session-request-submit-btn">
                {state.sending ? <Loader2 className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}{state.sending ? f.sending : f.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
